import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '../../utils';
import { PAGE_SIZES, type PageSize } from './types';

export interface DataTablePaginationProps {
  /** Zero-indexed current page. */
  page: number;
  onPageChange: (page: number) => void;
  pageSize: PageSize;
  onPageSizeChange: (size: PageSize) => void;
  /** Total rows across all pages. */
  total: number;
  className?: string;
}

export function DataTablePagination({
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  total,
  className,
}: DataTablePaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount - 1);
  const from = total === 0 ? 0 : current * pageSize + 1;
  const to = Math.min(total, (current + 1) * pageSize);
  const canPrev = current > 0;
  const canNext = current < pageCount - 1;

  return (
    <div
      className={cn(
        'flex items-center gap-4 flex-wrap',
        'px-3 py-2 border-t border-[var(--color-border)]',
        'text-[12px] text-[var(--color-text-secondary)] tabular-nums',
        className,
      )}
    >
      {/* Page-size selector */}
      <label className="flex items-center gap-1">
        <span>Rows:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.currentTarget.value) as PageSize)}
          className={cn(
            'h-7 px-1 rounded-[4px] border border-[var(--color-border)]',
            'bg-[var(--color-bg)] text-[var(--color-text-primary)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]',
          )}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>

      <span>
        {from}–{to} of {total}
      </span>

      {/* Nav buttons */}
      <div className="flex items-center gap-1 ml-auto">
        <PageButton onClick={() => onPageChange(0)} disabled={!canPrev} label="First">
          <ChevronsLeft className="size-4" />
        </PageButton>
        <PageButton onClick={() => onPageChange(current - 1)} disabled={!canPrev} label="Previous">
          <ChevronLeft className="size-4" />
        </PageButton>
        <span className="px-2">
          {current + 1} / {pageCount}
        </span>
        <PageButton onClick={() => onPageChange(current + 1)} disabled={!canNext} label="Next">
          <ChevronRight className="size-4" />
        </PageButton>
        <PageButton onClick={() => onPageChange(pageCount - 1)} disabled={!canNext} label="Last">
          <ChevronsRight className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  children,
  onClick,
  disabled,
  label,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-[4px]',
        'border border-[var(--color-border)] bg-[var(--color-bg)]',
        'text-[var(--color-text-secondary)]',
        'transition-colors hover:bg-[var(--color-surface)]',
        'disabled:opacity-40 disabled:pointer-events-none',
      )}
    >
      {children}
    </button>
  );
}
